import { v7 } from 'uuid';

/**
 * Helper class for generating and converting URL-safe short IDs based on UUIDv7.
 * Uses Base64URL encoding to create 22-character strings from 128-bit UUIDs.
 */
export class ShortIdHelper {

    /**
     * Generates a new UUIDv7 and returns it as Base64URL string (22 chars).
     * UUIDv7 is time-based, so short IDs are chronologically sortable.
     * @returns A 22-character Base64URL encoded short ID
     */
    public static generate(): string {
        return this.fromUuid(v7());
    }

    /**
     * Converts an existing UUID to Base64URL format.
     * @param uuid - UUID string with or without hyphens (e.g., "0195c8a0-1234-7def-8abc-123456789012")
     * @returns A 22-character Base64URL encoded short ID
     * @throws Error if UUID format is invalid
     */
    public static fromUuid(uuid: string): string {
        const hex = uuid.replace(/-/g, '');

        if (hex.length !== 32 || !/^[0-9a-fA-F]+$/.test(hex)) {
            throw new Error(`Invalid UUID format: ${uuid}`);
        }

        // Convert hex string to bytes
        const bytes = new Uint8Array(16);
        for (let i = 0; i < 16; i++) {
            bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
        }

        // Convert bytes to Base64URL
        const binaryString = String.fromCharCode(...Array.from(bytes));
        const base64 = btoa(binaryString);

        // Make URL-safe: replace + with -, / with _, and remove = padding
        return base64
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    /**
     * Checks if a string is a valid Base64URL short ID (22 chars).
     * @param value - The string to check
     * @returns true if value is a valid short ID format
     */
    public static isShortId(value: string): boolean {
        return value.length === 22 && /^[a-zA-Z0-9_-]+$/.test(value);
    }

    /**
     * Normalizes a UUID to standard 36-character format.
     * Handles legacy UUIDs with missing leading zero (35 chars → 36 chars).
     * @param uid - UUID string (35 or 36 characters with hyphens)
     * @returns Normalized UUID in 8-4-4-4-12 format
     */
    public static normalizeUuid(uid: string): string {
        // Standard UUID with hyphens: 8-4-4-4-12 = 36 chars
        if (uid.length === 36) {
            return uid;
        }

        // Legacy UUID with missing leading zero: 7-4-4-4-12 = 35 chars
        if (uid.length === 35) {
            return '0' + uid;
        }

        // Other formats - return as-is
        return uid;
    }

    /**
     * Converts a Base64URL short ID back to UUID format (with hyphens).
     * @param shortId - A 22-character Base64URL encoded short ID
     * @returns UUID string in standard format (e.g., "0195c8a0-1234-7def-8abc-123456789012")
     * @throws Error if shortId format is invalid
     */
    public static toUuid(shortId: string): string {
        if (shortId.length !== 22) {
            throw new Error(`Invalid short ID length: expected 22, got ${shortId.length}`);
        }

        // Restore standard Base64: replace - with +, _ with /, add padding
        const base64 = shortId
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            + '==';

        // Decode Base64 to binary string
        let binaryString: string;
        try {
            binaryString = atob(base64);
        } catch {
            throw new Error(`Invalid Base64URL short ID: ${shortId}`);
        }

        if (binaryString.length !== 16) {
            throw new Error(`Invalid short ID: decoded to ${binaryString.length} bytes, expected 16`);
        }

        // Convert binary string to hex
        let hex = '';
        for (let i = 0; i < 16; i++) {
            hex += binaryString.charCodeAt(i).toString(16).padStart(2, '0');
        }

        // Format as UUID with hyphens: 8-4-4-4-12
        return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20, 32)}`;
    }
}
